import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, FormControl, InputLabel, Select, MenuItem,
    Box, Typography, Alert, Autocomplete, Chip
} from '@mui/material';
import { apiClient } from 'utils/ApiClient';

const TEMPLATE_CATEGORIES = [
    { value: 'transactional', label: 'Transactional' },
    { value: 'promotional', label: 'Promotional' },
    { value: 'service', label: 'Service' },
    { value: 'system', label: 'System' },
];

type CountryOption = { code: string; label: string; dialCode: string };

interface SystemEmailRegisterDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const SystemEmailRegisterDialog: React.FC<SystemEmailRegisterDialogProps> = ({
    open, onClose, onSuccess
}) => {
    const [formData, setFormData] = useState({
        templateName: '',
        accessLevel: 'Public',
        emailSubject: '',
        category: '',
        country: '',
        keywords: [] as string[]
    });
    const [countries, setCountries] = useState<CountryOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && countries.length === 0) {
            apiClient.get('/auth/countries').then((res: any) => {
                if (Array.isArray(res.data)) setCountries(res.data);
            }).catch(() => {});
        }
    }, [open]);

    const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
        const value = event.target.value;
        if (field === 'templateName') {
            setFormData(prev => ({ ...prev, [field]: value.replace(/[^a-zA-Z0-9_-]/g, '') }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const validateForm = (): boolean => {
        if (!formData.templateName.trim()) { setError('Template Name is required'); return false; }
        if (!formData.emailSubject.trim()) { setError('Email Subject is required'); return false; }
        setError(null);
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);
        setError(null);
        try {
            await apiClient.post('/iu/systemtemplate/new', {
                templateName: formData.templateName,
                templateType: 'mail',
                accessLevel: formData.accessLevel.toLowerCase(),
                emailSubject: formData.emailSubject,
                templateContent: '<p>Email template content will be created using the Email Builder.</p>',
                sharedTo: [],
                mediaAttachments: [],
                ...(formData.category ? { category: formData.category } : {}),
                ...(formData.country ? { country: formData.country } : {}),
                ...(formData.keywords.length > 0 ? { keywords: formData.keywords } : {}),
            });
            setFormData({ templateName: '', accessLevel: 'Public', emailSubject: '', category: '', country: '', keywords: [] });
            onSuccess();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while creating the template');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => { if (!loading) { setError(null); onClose(); } };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography component="span" variant="h6">Create New Email System Template</Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
                    <TextField label="Template Name" value={formData.templateName}
                        onChange={handleInputChange('templateName')} fullWidth required
                        helperText="Only alphanumeric characters, underscores and hyphens allowed"
                        disabled={loading} />
                    <TextField label="Template Type" value="EMAIL" fullWidth disabled />
                    <FormControl fullWidth required>
                        <InputLabel id="sys-access-label">Access Level</InputLabel>
                        <Select labelId="sys-access-label" value={formData.accessLevel} label="Access Level"
                            onChange={handleInputChange('accessLevel')} disabled={loading}>
                            <MenuItem value="Public">Public</MenuItem>
                            <MenuItem value="Private">Private</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField label="Email Subject" value={formData.emailSubject}
                        onChange={handleInputChange('emailSubject')} fullWidth required disabled={loading} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <TextField
                            select
                            size="small"
                            label="Category"
                            value={formData.category}
                            onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
                            fullWidth
                            disabled={loading}
                            SelectProps={{
                                displayEmpty: true,
                                renderValue: (v) => v ? (TEMPLATE_CATEGORIES.find(c => c.value === v)?.label ?? String(v)) : 'Select category',
                                MenuProps: { PaperProps: { sx: { '& .MuiMenuItem-root': { py: 1 } } } },
                            }}
                            InputLabelProps={{ shrink: true }}
                        >
                            {TEMPLATE_CATEGORIES.map(cat => (
                                <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                            ))}
                        </TextField>
                        <Autocomplete
                            size="small"
                            options={countries}
                            getOptionLabel={(option) => `${option.label} (${option.dialCode})`}
                            value={countries.find(c => c.code === formData.country) || null}
                            onChange={(_e, newValue) => setFormData(prev => ({ ...prev, country: newValue?.code || '' }))}
                            renderInput={(params) => <TextField {...params} label="Country" />}
                            isOptionEqualToValue={(option, value) => option.code === value.code}
                            fullWidth
                            disabled={loading}
                        />
                    </Box>
                    <Autocomplete
                        multiple
                        freeSolo
                        size="small"
                        options={[]}
                        value={formData.keywords}
                        onChange={(_e, newValue) => setFormData(prev => ({ ...prev, keywords: newValue as string[] }))}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip size="small" label={option} {...getTagProps({ index })} key={index} />
                            ))
                        }
                        renderInput={(params) => <TextField {...params} label="Keywords" placeholder="Type and press Enter" />}
                        disabled={loading}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="outlined" color="secondary">Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="success"
                    disabled={loading || !formData.templateName || !formData.emailSubject}>
                    {loading ? 'Creating...' : 'Create Template'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
